import type {
  UserQuestion,
  UserQuestionAnswer,
  UserQuestionAnswerItem,
  UserQuestionItem,
} from "@app/lib/actions/types";

export const USER_QUESTION_DECLINED_MESSAGE =
  "User skipped the questions. Proceed with your stated default.";

// Actions declined before the skip wording changed keep this text in their stored output.
const LEGACY_USER_QUESTION_DECLINED_MESSAGE =
  "User declined to answer. Proceed with your best judgment.";

// A separate item from the answer itself, which the UI parses back: once the person has answered,
// the run is otherwise silent until the final reply, so they cannot tell their answer was heard.
export const USER_QUESTION_ACKNOWLEDGE_NOTE =
  "If you still need tools, first tell the person in one short sentence what is now settled and " +
  'what you are doing with it ("One month, paying design partners first: building the plan around that."), ' +
  "as plain chat, not a heading or list. If you can reply without tools, write only the reply: it " +
  "is the acknowledgement, so never add a separate sentence restating the answer.";

const ANSWER_PREFIX = 'User has answered your question: "';
const ANSWER_SUFFIX =
  "\". You can now continue with the user's answers in mind.";
const CUSTOM_ANSWER_PREFIX = "Other: ";
const RECOMMENDED_SUFFIX = /\s*\(Recommended\)\s*$/;

export function splitRecommendedLabel(label: string): {
  label: string;
  recommended: boolean;
} {
  return {
    label: label.replace(RECOMMENDED_SUFFIX, ""),
    recommended: RECOMMENDED_SUFFIX.test(label),
  };
}

export function recommendedOptionDescription(
  description: string | null | undefined,
  recommended: boolean
): string | null {
  if (!recommended) {
    return description ?? null;
  }
  return description ? `Recommended · ${description}` : "Recommended";
}

export function listQuestionItems(question: UserQuestion): UserQuestionItem[] {
  return [
    {
      question: question.question,
      options: question.options,
      multiSelect: question.multiSelect,
    },
    ...(question.additionalQuestions ?? []),
  ];
}

export function listAnswerItems(
  answer: UserQuestionAnswer
): UserQuestionAnswerItem[] {
  return [
    {
      selectedOptions: answer.selectedOptions,
      customResponse: answer.customResponse,
    },
    ...(answer.additionalAnswers ?? []),
  ];
}

export function getUserQuestionSelections(
  question: UserQuestionItem,
  answer: UserQuestionAnswerItem
): string[] {
  const selections: string[] = [];
  for (const idx of answer.selectedOptions) {
    if (idx >= 0 && idx < question.options.length) {
      selections.push(question.options[idx].label);
    }
  }
  if (answer.customResponse) {
    selections.push(`${CUSTOM_ANSWER_PREFIX}${answer.customResponse}`);
  }
  return selections;
}

function skippedLinePrefix(question: string): string {
  return `"${question}"=skipped, use your default: `;
}

function declinedLine(question: string): string {
  return `"${question}"=${USER_QUESTION_DECLINED_MESSAGE}`;
}

function hasLineStartingWith(outputText: string, lineStart: string): boolean {
  return (
    outputText.startsWith(lineStart) || outputText.includes(`\n${lineStart}`)
  );
}

export function formatUserQuestionToolResult(
  question: UserQuestion,
  answer: UserQuestionAnswer
): string {
  const answers = listAnswerItems(answer);
  return listQuestionItems(question)
    .map((item, i) => {
      const selections = answers[i]
        ? getUserQuestionSelections(item, answers[i])
        : [];
      if (selections.length > 0) {
        return formatUserQuestionAnswer(item.question, selections);
      }
      return question.defaultAssumption
        ? `${skippedLinePrefix(item.question)}${question.defaultAssumption}`
        : declinedLine(item.question);
    })
    .join("\n");
}

export function formatAnswerMessageContent(
  question: UserQuestion,
  answer: UserQuestionAnswer
): string | null {
  const answers = listAnswerItems(answer);
  const lines = listQuestionItems(question).map((item, i) => {
    const selections = answers[i]
      ? getUserQuestionSelections(item, answers[i])
      : [];
    return selections
      .map((s) =>
        (s.startsWith(CUSTOM_ANSWER_PREFIX)
          ? s.slice(CUSTOM_ANSWER_PREFIX.length)
          : s
        ).replace(RECOMMENDED_SUFFIX, "")
      )
      .join(", ");
  });
  if (lines.every((line) => line === "")) {
    return null;
  }
  if (lines.length === 1) {
    return lines[0];
  }
  return lines.map((line, i) => `${i + 1}. ${line || "Skipped"}`).join("\n");
}

export function formatUserQuestionAnswer(
  question: string,
  selections: string[]
): string {
  return `${ANSWER_PREFIX}${question}"="${selections.join(", ")}${ANSWER_SUFFIX}`;
}

// A tool result holds one answer line per question, and a custom response may span lines, so the
// asked question's line is found by its prefix and closed by the suffix at a line end.
function findAnsweredSelectionsText(
  outputText: string,
  question: string
): string | null {
  const prefix = `${ANSWER_PREFIX}${question}"="`;
  let selectionsStart: number;
  if (outputText.startsWith(prefix)) {
    selectionsStart = prefix.length;
  } else {
    const prefixAt = outputText.indexOf(`\n${prefix}`);
    if (prefixAt === -1) {
      return null;
    }
    selectionsStart = prefixAt + 1 + prefix.length;
  }

  const lineEnd = outputText.indexOf(`${ANSWER_SUFFIX}\n`, selectionsStart);
  if (lineEnd !== -1) {
    return outputText.slice(selectionsStart, lineEnd);
  }
  if (outputText.endsWith(ANSWER_SUFFIX)) {
    return outputText.slice(
      selectionsStart,
      outputText.length - ANSWER_SUFFIX.length
    );
  }
  return null;
}

export function parseUserQuestionAnswer(
  outputText: string,
  question: string
): {
  selectedLabels: string[];
  customAnswer: string | null;
  isDeclined: boolean;
  isSkippedWithDefault: boolean;
} {
  const selectedLabels: string[] = [];
  let customAnswer: string | null = null;

  const selectionsText = findAnsweredSelectionsText(outputText, question);
  if (selectionsText === null) {
    return {
      selectedLabels,
      customAnswer,
      isDeclined:
        outputText === USER_QUESTION_DECLINED_MESSAGE ||
        outputText === LEGACY_USER_QUESTION_DECLINED_MESSAGE ||
        hasLineStartingWith(outputText, declinedLine(question)),
      isSkippedWithDefault: hasLineStartingWith(
        outputText,
        skippedLinePrefix(question)
      ),
    };
  }

  for (const part of selectionsText.split(", ")) {
    if (part.startsWith(CUSTOM_ANSWER_PREFIX)) {
      customAnswer = part;
    } else {
      selectedLabels.push(part);
    }
  }

  return {
    selectedLabels,
    customAnswer,
    isDeclined: false,
    isSkippedWithDefault: false,
  };
}
